import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import BoardingSlaService from '_services/boarding-sla-service';

export const fetchBoardingSla = createAsyncThunk(
  'boardingSla/fetch',
  async (params, { signal }) => {
    return await BoardingSlaService.getBoardingSla(params, signal);
  },
  {
    condition: (_params, { getState }) => {
      if (getState().boardingSla.status === 'loading') return false;
    },
  }
);

export const patchBoardingOverride = createAsyncThunk(
  'boardingSla/patchOverride',
  async ({ bookingId, bookingIds, kpiKey, pass, passReason, reasonCode, reasonDetail, ignore }) => {
    const ids = [...new Set((bookingIds?.length ? bookingIds : [bookingId]).filter(Boolean))];
    let overrideValue;
    const reasonFields = { passReason, reason: passReason, reasonCode, reasonDetail };
    if (ignore) {
      overrideValue = { ignore: true, pass: null, ...reasonFields };
    } else {
      overrideValue = { ignore: false, pass, ...reasonFields };
    }
    const patch = kpiKey
      ? { boardingSlaOverride: { [kpiKey]: overrideValue } }
      : { boardingSlaOverride: overrideValue };
    await Promise.all(ids.map((id) => BoardingSlaService.patchBooking(id, patch)));
    return { bookingIds: ids };
  }
);

const initialState = {
  data: null,
  status: 'idle',
  error: null,
  patchStatus: 'idle',
};

const boardingSlaSlice = createSlice({
  name: 'boardingSla',
  initialState,
  reducers: {
    resetBoardingSla: (state) => {
      state.data = null;
      state.status = 'idle';
      state.error = null;
    },
    resetPatchBoardingOverrideStatus: (state) => {
      state.patchStatus = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBoardingSla.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchBoardingSla.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data = action.payload;
        state.error = null;
      })
      .addCase(fetchBoardingSla.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(patchBoardingOverride.pending, (state) => {
        state.patchStatus = 'loading';
      })
      .addCase(patchBoardingOverride.fulfilled, (state) => {
        state.patchStatus = 'succeeded';
      })
      .addCase(patchBoardingOverride.rejected, (state) => {
        state.patchStatus = 'failed';
      });
  },
});

export const selectBoardingSlaData = (state) => state.boardingSla.data;
export const selectBoardingSlaStatus = (state) => state.boardingSla.status;
export const selectBoardingSlaError = (state) => state.boardingSla.error;
export const selectPatchBoardingOverrideStatus = (state) => state.boardingSla.patchStatus;

export const { resetBoardingSla, resetPatchBoardingOverrideStatus } = boardingSlaSlice.actions;

export default boardingSlaSlice.reducer;
